import { ExemptionData, ExemptionDataEntry } from "./types";

// Adding type declaration for the function property
declare global {
  interface Function {
    cachedData?: any;
  }
}

// Interface for the data format in the JSON files
interface RawExemptionDataPoint {
  SchoolCode: number;
  [year: string]: number | string | null;
}

/**
 * Loads the personal belief exemption data
 * @returns A promise that resolves to personal belief exemption data
 */
export async function loadPersonalBeliefExemptions(): Promise<
  RawExemptionDataPoint[]
> {
  // Use memoization to avoid loading the data multiple times
  if (loadPersonalBeliefExemptions.cachedData) {
    console.log("Using cached PBE data");
    return loadPersonalBeliefExemptions.cachedData;
  }

  try {
    console.log("Fetching PBE data from /files/VaxApp-PBE-Min.json");
    // Path for public file access
    const response = await fetch("./files/VaxApp-PBE-Min.json");
    if (!response.ok) {
      throw new Error(
        `Failed to load PBE data: ${response.status} ${response.statusText}`
      );
    }

    const jsonData = await response.json();
    console.log("PBE data loaded successfully, entries:", jsonData.length);
    loadPersonalBeliefExemptions.cachedData = jsonData;
    return jsonData;
  } catch (error) {
    console.error("Error loading personal belief exemption data:", error);
    // Return empty array as fallback
    return [];
  }
}

/**
 * Loads the medical exemption data
 * @returns A promise that resolves to medical exemption data
 */
export async function loadMedicalExemptions(): Promise<
  RawExemptionDataPoint[]
> {
  // Use memoization to avoid loading the data multiple times
  if (loadMedicalExemptions.cachedData) {
    console.log("Using cached PME data");
    return loadMedicalExemptions.cachedData;
  }

  try {
    console.log("Fetching PME data from /files/VaxApp-PME-Min-20250319.json");
    // Path for public file access
    const response = await fetch("./files/VaxApp-PME-Min-20250319.json");
    if (!response.ok) {
      throw new Error(
        `Failed to load PME data: ${response.status} ${response.statusText}`
      );
    }

    const jsonData = await response.json();
    console.log("PME data loaded successfully, entries:", jsonData.length);
    loadMedicalExemptions.cachedData = jsonData;
    return jsonData;
  } catch (error) {
    console.error("Error loading medical exemption data:", error);
    // Return empty array as fallback
    return [];
  }
}

/**
 * Parses numeric values from the exemption data, handling string values
 */
function parseExemptionValue(value: number | string | null): number | null {
  if (value === null || value === "") return null;
  if (typeof value === "string") {
    // Handle strings like "5", "10", etc.
    if (value.trim() === "" || value.includes(".")) return null;
    const parsedValue = parseFloat(value);
    return isNaN(parsedValue) ? null : parsedValue;
  }
  return value;
}

/**
 * Transforms raw exemption data into the format expected by the application
 */
function transformRawExemptionData(
  rawData: RawExemptionDataPoint[]
): ExemptionData {
  const result: ExemptionData = {};

  const allYears = [
    "2010-11",
    "2011-12",
    "2012-13",
    "2013-14",
    "2014-15",
    "2015-16",
    "2016-17",
    "2017-18",
    "2018-19",
    "2019-20",
    "2020-21",
    "2021-22",
    "2022-23",
    "2023-24",
  ];

  rawData.forEach((item) => {
    const schoolCode = item.SchoolCode.toString();

    if (!result[schoolCode]) {
      result[schoolCode] = [];
    }

    allYears.forEach((year) => {
      if (year in item) {
        const value = parseExemptionValue(item[year]);
        if (value !== null) {
          // Check if we already have this year for this school
          const existingEntryIndex = result[schoolCode].findIndex(
            (entry) => entry.year === year
          );

          if (existingEntryIndex >= 0) {
            // Update existing entry
            result[schoolCode][existingEntryIndex].personalBeliefExemptions =
              value;
          } else {
            // Add new entry
            result[schoolCode].push({
              year,
              personalBeliefExemptions: value,
              medicalExemptions: 0, // Default, will be updated if medical data exists
            });
          }
        }
      }
    });
  });

  return result;
}

/**
 * Loads both personal belief and medical exemption data and combines them
 * @returns A promise that resolves to a combined ExemptionData object
 */
export async function loadAllExemptionData(): Promise<ExemptionData> {
  try {
    console.log("Loading PBE and PME data...");
    const [pbeRawData, pmeRawData] = await Promise.all([
      loadPersonalBeliefExemptions(),
      loadMedicalExemptions(),
    ]);

    console.log("PBE data entries:", pbeRawData.length);
    console.log("PME data entries:", pmeRawData.length);

    // Transform PBE data
    const pbeData = transformRawExemptionData(pbeRawData);
    console.log(
      "Transformed PBE data for schools:",
      Object.keys(pbeData).length
    );

    // Create combined data starting with PBE
    const combinedData: ExemptionData = { ...pbeData };

    // Process PME data and merge with PBE data
    pmeRawData.forEach((item) => {
      const schoolCode = item.SchoolCode.toString();
      const allYears = [
        "2010-11",
        "2011-12",
        "2012-13",
        "2013-14",
        "2014-15",
        "2015-16",
        "2016-17",
        "2017-18",
        "2018-19",
        "2019-20",
        "2020-21",
        "2021-22",
        "2022-23",
        "2023-24",
      ];

      if (!combinedData[schoolCode]) {
        combinedData[schoolCode] = [];
      }

      allYears.forEach((year) => {
        if (year in item) {
          const value = parseExemptionValue(item[year]);
          if (value !== null) {
            // Check if this year already exists for this school
            const existingEntryIndex = combinedData[schoolCode].findIndex(
              (entry) => entry.year === year
            );

            if (existingEntryIndex >= 0) {
              // Update existing entry with medical exemption data
              combinedData[schoolCode][existingEntryIndex].medicalExemptions =
                value;
            } else {
              // Add new entry with only medical exemption data
              combinedData[schoolCode].push({
                year,
                personalBeliefExemptions: 0,
                medicalExemptions: value,
              });
            }
          }
        }
      });
    });

    console.log(
      "Combined data created with schools:",
      Object.keys(combinedData).length
    );

    if (Object.keys(combinedData).length > 0) {
      const sampleSchool = Object.keys(combinedData)[0];
      console.log(
        "Sample data for school",
        sampleSchool,
        ":",
        combinedData[sampleSchool]
      );
    }

    return combinedData;
  } catch (error) {
    console.error("Error loading combined exemption data:", error);
    return {};
  }
}
